import type { ProductFeatureDefinition } from "./product-feature-definition";

/**
 * Catalog entries for CRMs and integrations, the MCP connector, settings, onboarding, support,
 * the Chrome extension and the staff-only pages (PREC-4329).
 *
 * A CRM is one feature per provider (connect, configure, its own sync): the dashboard compares the
 * providers. `crm-sync` is the provider-agnostic surface the MCP tools read and push through.
 * Staff pages are features of the Admin area, so staff use is visible and a dashboard can leave
 * the area out. `app` is the use of the product as a whole: the server records one `app`/`active`
 * event per user and UTC day, on the surface of that day's first request (PREC-4335).
 */
export const PLATFORM_PRODUCT_FEATURES = [
	{
		key: "crm-hubspot",
		label: "HubSpot",
		area: "CRM",
		surfaces: ["web", "api"],
		featureCode: "HUBSPOT",
	},
	{
		key: "crm-zoho",
		label: "Zoho CRM",
		area: "CRM",
		surfaces: ["web", "api"],
		featureCode: "ZOHO_CRM",
	},
	{
		key: "crm-pipedrive",
		label: "Pipedrive",
		area: "CRM",
		surfaces: ["web", "api"],
		featureCode: "PIPEDRIVE_CRM",
	},
	{
		key: "crm-msdynamics",
		label: "Microsoft Dynamics",
		area: "CRM",
		surfaces: ["web", "api"],
		featureCode: "MSDYNAMICS_CRM",
	},
	{
		key: "crm-salesforce",
		label: "Salesforce",
		area: "CRM",
		surfaces: ["web", "api"],
		featureCode: "SALESFORCE_CRM",
	},
	{ key: "crm-sync", label: "CRM sync", area: "CRM", surfaces: ["web", "mcp", "api"] },
	{
		key: "integrations-overview",
		label: "Integrations overview",
		area: "Integrations",
		surfaces: ["web"],
	},
	{ key: "webhooks", label: "Webhooks", area: "Integrations", surfaces: ["web", "api"] },
	{ key: "public-api", label: "Public API", area: "Integrations", surfaces: ["web", "api"] },
	{ key: "lemlist", label: "lemlist", area: "Integrations", surfaces: ["web", "api"] },
	{ key: "n8n", label: "n8n", area: "Integrations", surfaces: ["web", "api"] },
	{ key: "apollo-api-key", label: "Apollo API key", area: "Integrations", surfaces: ["web"] },
	{
		key: "mcp-connector",
		label: "MCP connector",
		area: "MCP",
		surfaces: ["web", "mcp"],
		featureCode: "MCP_ACCESS",
	},
	{
		key: "account-settings",
		label: "Personal settings",
		area: "Settings",
		surfaces: ["web", "mcp"],
	},
	{
		key: "mailbox-connection",
		label: "Mailbox connection",
		area: "Settings",
		surfaces: ["web"],
	},
	{
		key: "linkedin-account",
		label: "LinkedIn account connection",
		area: "Settings",
		surfaces: ["web", "extension"],
	},
	{
		key: "linkedin-connection-sync",
		label: "LinkedIn connection sync",
		area: "Settings",
		surfaces: ["web", "extension"],
		featureCode: "CONNECTION_SYNC",
	},
	{
		key: "linkedin-automation",
		label: "LinkedIn automation (auto-accept, auto-withdraw, profile visits)",
		area: "Settings",
		surfaces: ["web"],
	},
	{
		key: "workspace-settings",
		label: "Workspace settings",
		area: "Settings",
		surfaces: ["web"],
	},
	{ key: "user-management", label: "Users and roles", area: "Settings", surfaces: ["web", "mcp"] },
	{ key: "billing", label: "Subscription and credits", area: "Settings", surfaces: ["web", "mcp"] },
	{
		key: "notifications",
		label: "Notifications",
		area: "Settings",
		surfaces: ["web", "mcp"],
		featureCode: "NOTIFICATION_CENTER",
	},
	{ key: "onboarding", label: "Onboarding", area: "Onboarding", surfaces: ["web"] },
	{
		key: "product-tours",
		label: "Product tours",
		area: "Onboarding",
		surfaces: ["web"],
		featureCode: "FEATURE_ONBOARDING",
	},
	{
		key: "help-assistant",
		label: "Help assistant and articles",
		area: "Support",
		surfaces: ["web"],
		featureCode: "HELP_CENTER",
	},
	{
		key: "support-tickets",
		label: "Support tickets",
		area: "Support",
		surfaces: ["web"],
		featureCode: "SUPPORT_TICKETS",
	},
	{ key: "changelog", label: "Changelog", area: "Support", surfaces: ["web"] },
	{
		key: "chrome-extension",
		label: "Chrome extension",
		area: "Extension",
		surfaces: ["extension"],
	},
	{
		key: "extension-profile-panel",
		label: "LinkedIn profile panel",
		area: "Extension",
		surfaces: ["extension"],
	},
	{ key: "admin-companies", label: "Staff: companies", area: "Admin", surfaces: ["web"] },
	{
		key: "admin-feature-management",
		label: "Staff: feature management",
		area: "Admin",
		surfaces: ["web"],
	},
	{ key: "admin-support", label: "Staff: support queue", area: "Admin", surfaces: ["web"] },
	{
		key: "admin-help-conversations",
		label: "Staff: help conversations",
		area: "Admin",
		surfaces: ["web"],
	},
	{
		key: "admin-operations",
		label: "Staff: operations (cron, API health, fixes)",
		area: "Admin",
		surfaces: ["web"],
	},
	{
		key: "admin-global-blacklist",
		label: "Staff: global blacklist",
		area: "Admin",
		surfaces: ["web"],
	},
	{
		key: "admin-custom-events",
		label: "Staff: event scraping requests",
		area: "Admin",
		surfaces: ["web"],
	},
	{
		key: "app",
		label: "App (daily active users)",
		area: "App",
		surfaces: ["web", "extension", "mcp", "api"],
	},
] as const satisfies readonly ProductFeatureDefinition[];
